import React from 'react';
import { Shield, Lock, FileWarning, FilePen } from 'lucide-react';

/**
 * Protection category icons and colors
 */
const PROTECTION_CONFIG = {
  work_product: {
    icon: Shield,
    color: 'text-red-400',
    bg: 'bg-red-500/20',
    border: 'border-red-500/30',
    label: 'Work Product'
  },
  privilege: {
    icon: Lock,
    color: 'text-amber-400',
    bg: 'bg-amber-500/20',
    border: 'border-amber-500/30',
    label: 'Privileged'
  },
  confidential: {
    icon: FileWarning,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/20',
    border: 'border-cyan-500/30',
    label: 'Confidential'
  },
  draft: {
    icon: FilePen,
    color: 'text-slate-400',
    bg: 'bg-slate-500/20',
    border: 'border-slate-500/30',
    label: 'Draft'
  }
};

/**
 * Single protection badge
 */
export const ProtectionBadge = ({ code, showLabel = true, size = 'sm' }) => {
  const config = PROTECTION_CONFIG[code];
  if (!config) return null;

  const Icon = config.icon;
  const iconSize = size === 'sm' ? 12 : size === 'md' ? 14 : 16;
  const textSize = size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base';
  const padding = size === 'sm' ? 'px-1.5 py-0.5' : size === 'md' ? 'px-2 py-1' : 'px-3 py-1.5';

  return (
    <span
      className={`inline-flex items-center gap-1 ${padding} ${config.bg} ${config.color} ${textSize} rounded border ${config.border}`}
      title={config.label}
    >
      <Icon size={iconSize} />
      {showLabel && <span>{config.label}</span>}
    </span>
  );
};

/**
 * Display multiple protection badges for a document
 */
const ProtectionBadges = ({ protections = [], showLabels = false, size = 'sm', onClick }) => {
  if (!protections || protections.length === 0) return null;

  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick();
    }
  };

  return (
    <div
      className={`flex items-center gap-1 flex-wrap ${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}
      onClick={handleClick}
    >
      {protections.map((protection, idx) => (
        <ProtectionBadge
          key={protection.category_code || idx}
          code={protection.category_code}
          showLabel={showLabels}
          size={size}
        />
      ))}
    </div>
  );
};

/**
 * Protection indicator icon for document lists
 * Shows a single shield icon with count if multiple protections
 */
export const ProtectionIndicator = ({ protections = [], onClick }) => {
  if (!protections || protections.length === 0) return null;

  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick();
    }
  };

  // Get the most important protection to show
  const primaryProtection = protections[0];
  const config = PROTECTION_CONFIG[primaryProtection?.category_code];

  if (!config) return null;

  const Icon = config.icon;
  const count = protections.length;

  return (
    <button
      onClick={handleClick}
      className={`relative p-1 rounded ${config.bg} ${config.color} hover:opacity-80 transition-opacity`}
      title={protections.map(p => PROTECTION_CONFIG[p.category_code]?.label || p.category_code).join(', ')}
    >
      <Icon size={14} />
      {count > 1 && (
        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-slate-700 text-white text-[10px] rounded-full flex items-center justify-center border border-slate-600">
          {count}
        </span>
      )}
    </button>
  );
};

export default ProtectionBadges;
